import { Link } from "@/components/link";

interface SellerBadgeProps {
  username?: string | null;
  avatarUrl?: string | null;
  name?: string | null;
  size?: "sm" | "md";
  disableLink?: boolean;
}

function sellerBadgePresentation(input: SellerBadgeProps) {
  const identity = input.username ?? input.name ?? "?";
  return {
    avatarSize: input.size === "md" ? 24 : 16,
    displayText: input.username ? `@${input.username}` : (input.name ?? ""),
    initial: identity.charAt(0).toUpperCase(),
  };
}

function SellerAvatar({
  avatarSize,
  avatarUrl,
  displayText,
  initial,
}: {
  avatarSize: number;
  avatarUrl?: string | null;
  displayText: string;
  initial: string;
}) {
  return (
    <span
      className="shrink-0 rounded-full bg-muted flex items-center justify-center overflow-hidden"
      style={{ width: avatarSize, height: avatarSize }}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={displayText}
          width={avatarSize}
          height={avatarSize}
          className="w-full h-full object-cover"
        />
      ) : (
        <span
          className="text-muted-foreground font-medium leading-none"
          style={{ fontSize: avatarSize * 0.55 }}
        >
          {initial}
        </span>
      )}
    </span>
  );
}

function SellerIdentity({ name, username }: Pick<SellerBadgeProps, "name" | "username">) {
  if (!username) return <span>{name}</span>;
  return (
    <span>
      <span className="text-muted-foreground">@</span>
      {username}
    </span>
  );
}

export function SellerBadge({
  username,
  avatarUrl,
  name,
  size = "sm",
  disableLink,
}: SellerBadgeProps) {
  const { avatarSize, displayText, initial } = sellerBadgePresentation({
    avatarUrl,
    disableLink,
    name,
    size,
    username,
  });

  if (!displayText) return null;

  const content = (
    <span
      className={`inline-flex items-center gap-1.5 text-sm ${username ? "hover:underline" : ""}`}
    >
      <SellerAvatar {...{ avatarSize, avatarUrl, displayText, initial }} />
      <SellerIdentity name={name} username={username} />
    </span>
  );

  if (username && !disableLink) {
    return (
      <Link
        to={`/u/${username}`}
        className="inline-flex items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </Link>
    );
  }

  return content;
}
